import { Model } from "./Model";
import { IEdge } from "./IEdge";
import { IFace } from "./IFace";
import { IBody } from "./IBody";
import * as THREE from "three";
import { useMainStore } from "@/store/mainStore";
import { IBrep } from "./IBrep";
import { ITaskDto } from "./ITaskDto";
import { BlexonErpApi } from "@/store/BlexonErpApi";
import { EditorMode, LoadingState, MessageState, MessageType, Schweregrad } from "./Enums";
import { Line2 } from "three/examples/jsm/lines/Line2.js";
import { LineMaterial } from "three/examples/jsm/lines/LineMaterial";
import { LineGeometry } from "three/examples/jsm/lines/LineGeometry";
import { computeBoundsTree, disposeBoundsTree } from "three-mesh-bvh";
import i18n from "@/plugins/i18n";
import { PulseHighlighter } from "./PulseHighlighter";

// Add the extension functions
THREE.BufferGeometry.prototype.computeBoundsTree = computeBoundsTree;
THREE.BufferGeometry.prototype.disposeBoundsTree = disposeBoundsTree;

/**
 * Main class of the cam system
 */
export class Cam {
	public sceneGroup: THREE.Group | null = null;
	public model: Model = new Model();
	public hoveredGroupIds: number[] = [];
	public lastHoveredGroupIds: number[] = [];
	public lineHoverColor: THREE.Color = new THREE.Color(0xee00ee);
	public lineNormalColor: THREE.Color = new THREE.Color(0x000000);
	public lineSelectedColor: THREE.Color = new THREE.Color(0xff0000);
	public faceHoverColor: THREE.Color = new THREE.Color(0xee00ee);
	public faceSelectedColor: THREE.Color = new THREE.Color(0xd2042d);
	public store = useMainStore();
	public index = 0;
	public displayedBodies: IBody[] = [];
	public allFaces: IFace[] = [];
	public allEdges: IEdge[] = [];
	public bendBodies: IBody[] = [];

	// Id to keep track of latest measurement request
	private lastMeasurementRequestId: number = 0;

	// Pulsating red dot to help locate small highlighted faces.
	public pulseHighlighter: PulseHighlighter = new PulseHighlighter({
		getSceneGroup: () => this.sceneGroup,
		getZoom: () => this.store.zoom,
		getModelExtent: () =>
			Math.max(this.model.height || 0, this.model.width || 0, this.model.length || 0),
	});

	/**
	 * Loads the model from the erp, transforms it and sets it to the state
	 */
	public async loadModel(articleId: number) {
		const model = await BlexonErpApi.getModel(
			articleId,
			false,
			0,
			this.store.article.isAssembly
		);

		// Normalize export ids
		// Export ids in spaceclaim are increased on every export.
		// Due to this, the export ids in the features are not consistent
		// If we normalize the ids, we can still find them.

		this.index = 0;
		this.model = model;
    	model.bodies.forEach(b => b.show = true);

		this.setDisplayedBodies();

		const response = await BlexonErpApi.getModel(articleId, true, model.lastGroupId, false)

		this.bendBodies = response.bodies.flatMap((b) => b.bends);

		// After the reload after a modification, the edge id could have changed.
		// That will trigger a reload.
		if (this.store.rollDirection) {
			this.store.rollDirection.edgeId = null;
		}
		this.store.rollDirectionLoaded = false;
		this.addPressJoinBodiesToFeatures();

	}

	/**
	 * Add the export ids of faces and edges of the press join bodies to the
	 * corresponding features.
	 * This cannot be done in loadModel, as the features are loaded later.
	 */
	public addPressJoinBodiesToFeatures() {
		for (const body of this.model.bodies.filter(
			(b) => b.normalizedPressJoinFeatureFaceExportId != null
		)) {
			const feature = this.store.features.find((f) =>
				f.exportIds.some((id) => id === body.normalizedPressJoinFeatureFaceExportId)
			);
			if (feature != null) {
				feature.exportIds.push(...body.faces.map((f) => f.id));
			}
		}
	}

	/**
	 * Sets the bodies to be displayed.
	 */
	public setDisplayedBodies() {
		let newDisplayedBodies = this.model.bodies.filter(b => b.show);
		if (this.store.showBendzone) {
			newDisplayedBodies = newDisplayedBodies.concat(this.bendBodies);
		}
		this.displayedBodies = newDisplayedBodies;
		this.allEdges = this.displayedBodies.flatMap((b) => b.edges);
		this.allFaces = this.displayedBodies.flatMap((b) => b.faces);
	}

	/**
	 * Calls the backend to modify the model by the specified tasks.
	 */
	public async modifyFeatures(state: LoadingState, tasks: ITaskDto[]) {
		console.log("modifying features in cam...");
		try {
			this.store.setLoadingState(state);
			const response = await BlexonErpApi.modifyFeatures({
				articleId: this.store.article.id,
				tasks: tasks,
				skipValidation: this.store.skipValidation,
			});
			this.store.setAllWeldlinesVisibility(false); // Ausblenden, damit sie nachher sicher wieder eingeblendet werden. Sonst gibt es keinen Change.
			this.store.emptySelectedFeatures();
			await this.loadModel(this.store.article.id);
			this.store.replaceFeatures(response.features);
			this.addPressJoinBodiesToFeatures();
			this.store.setAllWeldlinesVisibility(this.store.editorMode === EditorMode.WeldMode);
			this.store.isFileSaved = false;
			let errorMessages: string[] = [];
			for (const task of response.tasks.filter(
				(t) => t.errorMessage && t.errorMessage.length
			)) {
				if (!errorMessages.includes(task.errorMessage!)) {
					errorMessages.push(task.errorMessage!);
				}
			}
			this.store.replaceValidationMessages(response.messages);
			if (errorMessages.length) {
				// Some errors contain "<br>". Create multiple messages for those.
				let allMessages: string[] = [];
				errorMessages.forEach((m: string) => {
					const messages: string[] = m
						.split("<br>")
						.filter((m: string) => m.trim() !== "");
					allMessages.push(...messages);
				});
				this.store.errorDialog = {
					title: i18n.global.t("common.errors.featureCreationFailedTitle"),
					messages: allMessages,
				};
			}
		} catch (error: any) {
			this.store.errorDialog = {
				title: i18n.global.t("common.errors.editingErrorTitle"),
				messages: [error],
			};
			const messages = [];
			messages.push({
				messageType: MessageType.FatalerFehler,
				title: i18n.global.t("common.errors.modelEditingFailedTitle"),
				featureIds: [],
				state: MessageState.Open,
				severity: Schweregrad.Error,
				helpLink: "",
				shortDescription: i18n.global.t("common.errors.modificationFailedShortDescription"),
			});
			this.store.replaceValidationMessages(messages);
		} finally {
			this.store.setLoadingState(LoadingState.Done);
		}
	}

	/**
	 * Sets the hover color to the hovered element.
	 * @param newGroupId GroupId of element where the mouse pointer is.
	 * This is -1 when nothing is hovered.
	 */
	public updateHoverColors(newGroupId: number, objectType?: string) {
		// If the group id is -1 (nothing is hovered)
		// and the hoveredGroupIds array is already empty
		// then everything has already been decolored. Nothing to do.
		// If the group id is already in the hovered group ids array,
		// nothing has to be done.
		if (
			(newGroupId === -1 && this.hoveredGroupIds.length === 0) ||
			this.hoveredGroupIds.includes(newGroupId)
		) {
			return;
		}

		if (!this.hoveredGroupIds.includes(newGroupId)) {
			this.hoveredGroupIds.push(newGroupId);
			if (objectType === "face") {
				this.setFaceColorById(newGroupId, this.faceHoverColor);
			} else {
				this.setEdgeColorById(newGroupId, this.lineHoverColor);
			}
		}

		this.hoveredGroupIds.forEach((groupId) => {
			if (newGroupId !== groupId) {
				if (!this.store.selectedGroupIds.includes(groupId)) {
					this.setFaceColorById(groupId, null);
					this.setEdgeColorById(groupId, null);
				} else {
					this.setFaceColorById(groupId, this.faceSelectedColor);
					this.setEdgeColorById(groupId, this.lineSelectedColor);
				}
			}
		});

		if (newGroupId > -1) {
			this.hoveredGroupIds = [newGroupId];
		} else {
			this.hoveredGroupIds = [];
		}
	}

	/**
	 * Sets the color of the given face.
	 */
	private setFaceColor(face: IFace, color: THREE.Color | null) {
		let colorToApply;
		if (color === null) {
			colorToApply = this.getColor(face.argbColor).toArray();
		} else {
			colorToApply = color.toArray();
		}
		// 3 values per vertex
		var colors = new Float32Array(face.mesh.geometry.attributes.position.count * 3);
		// Assign color to each vertex
		for (let i = 0; i < colors.length; i += 3) {
			colors[i] = colorToApply[0]; // Red
			colors[i + 1] = colorToApply[1]; // Green
			colors[i + 2] = colorToApply[2]; // Blue
		}
		face.mesh.geometry.setAttribute("color", new THREE.BufferAttribute(colors, 3));
		const colorAttribute = face.mesh.geometry.getAttribute("color");
		colorAttribute.needsUpdate = true;
	}
	/**
	 * Sets the color of the face with the given group id to the given color.
	 * If group id is < 1, nothing is set.
	 */
	public setFaceColorById(groupId: number, color: THREE.Color | null) {
		const face = this.allFaces.find((f) => f.groupId === groupId);
		if (face) {
			this.setFaceColor(face, color);
		}
	}
	/**
	 * Sets the color of all specified faces.
	 */
	public setFacesColor(faces: IFace[], color: THREE.Color | null) {
		faces.forEach((f) => {
			this.setFaceColor(f, color);
		});
	}

	/**
	 * This colors the edge and also makes it thicker if a color is given
	 */
	private setEdgeColor(edge: IEdge, color: THREE.Color | null) {
		// ** FOR LINE2 ** //
		edge.mesh.material.color = color ?? this.lineNormalColor;
		edge.mesh.material.linewidth = color ? 5 : 1.5;

		// ** FOR LINES ** //
		// const colorToApply = color?.toArray() ?? this.lineNormalColor.toArray();
		// const colors = new Float32Array(edge.mesh.geometry.attributes.position.count * 3);
		// for (let i = 0; i < colors.length; i += 3) {
		//     colors[i] = colorToApply[0]; // Red
		//     colors[i + 1] = colorToApply[1]; // Green
		//     colors[i + 2] = colorToApply[2]; // Blue
		// }
		// edge.mesh.geometry.setAttribute("color", new THREE.BufferAttribute(colors, 3));
		// const colorAttribute = edge.mesh.geometry.getAttribute("color");
		// colorAttribute.needsUpdate = true;
	}
	/**
	 * Sets the color of the edge with the given group id to the given color.
	 * If group id is < 1, nothing is set.
	 */
	public setEdgeColorById(groupId: number, color: THREE.Color | null) {
		const edge = this.allEdges.find((e) => e.groupId === groupId);
		if (edge) {
			this.setEdgeColor(edge, color);
		}
	}
	/**
	 * Sets the color of all specified edges.
	 */
	public setEdgesColor(edges: IEdge[], color: any) {
		edges.forEach((e) => {
			this.setEdgeColor(e, color);
		});
	}

	/**
	 * Sets the color of the specified elements and adds it to the selected
	 * list.
	 * @param exportIds
	 */
	public selectAndColorByExportId(exportIds: string[]) {
		let toRemoveGroups = this.store.selectedGroupIds.slice();

		const selectedEdges = this.allEdges.filter((e) => exportIds.includes(e.id));
		this.setEdgesColor(selectedEdges, this.lineSelectedColor);
		const edgeGroupIds = selectedEdges.flatMap((e) => e.groupId);
		this.store.selectedGroupIds.push(...edgeGroupIds);
		toRemoveGroups = toRemoveGroups.filter((g) => !edgeGroupIds.includes(g));

		const selectedFaces = this.allFaces.filter((e) => exportIds.includes(e.id));
		this.setFacesColor(selectedFaces, this.faceSelectedColor);
		const faceGroupIds = selectedFaces.flatMap((f) => f.groupId);
		this.store.selectedGroupIds.push(...faceGroupIds);
		toRemoveGroups = toRemoveGroups.filter((g) => !faceGroupIds.includes(g));

		// Deselect previous selected elements
		const deselectEdges = this.allEdges.filter((e) => toRemoveGroups.includes(e.groupId));
		this.setEdgesColor(deselectEdges, null);

		const toRemoveFaces = this.allFaces.filter((e) => toRemoveGroups.includes(e.groupId));
		this.setFacesColor(toRemoveFaces, null);

		// Die nicht mehr selektierten Gruppen auch im Store entfernen
		const finalSelectedGroupIds = this.store.selectedGroupIds.filter(
			(g) => !toRemoveGroups.includes(g)
		);
		this.store.selectedGroupIds.splice(
			0,
			this.store.selectedGroupIds.length,
			...finalSelectedGroupIds
		);
	}

	/**
	 * Is called on mouse click
	 * @returns
	 */
	public addHoverToSelected() {
		for (const hoveredGroupId of this.hoveredGroupIds) {
			if (this.store.editorMode === EditorMode.FeatureMode) {
				const allBreps: IBrep[] = (this.allEdges as IBrep[]).concat(this.allFaces);
				const brep = allBreps.find((f) => f.groupId == hoveredGroupId);
				const feature = this.store.features.find((f) =>
					f.exportIds.some((id) => id == brep?.id)
				);
				if (feature) {
					if (this.store.selectedFeatures.some((f) => f == feature)) {
						this.store.selectedFeatures.splice(
							this.store.selectedFeatures.indexOf(feature),
							1
						);
					} else {
						this.store.selectedFeatures.push(feature);
					}
				}
			}

			if (this.store.editorMode === EditorMode.MeasuringMode) {
				const indexOfId = this.store.selectedGroupIds.indexOf(hoveredGroupId);
				if (indexOfId === -1) {
					this.store.selectedGroupIds.push(hoveredGroupId);
					this.setFaceColorById(hoveredGroupId, this.faceSelectedColor);
					this.setEdgeColorById(hoveredGroupId, this.lineSelectedColor);
				} else {
					// Deselektieren
					this.store.selectedGroupIds.splice(indexOfId, 1);
					this.setFaceColorById(hoveredGroupId, null);
					this.setEdgeColorById(hoveredGroupId, null);
				}

				const selectedFaces: IBrep[] = this.allFaces.filter((f) =>
					this.store.selectedGroupIds.some((gid) => gid === f.groupId)
				);

				const selectedEdges = this.allEdges.filter((f) =>
					this.store.selectedGroupIds.some((gid) => gid === f.groupId)
				);

				if (selectedFaces.length > 0 || selectedEdges.length > 0) {
					this.store.loadingMeasurements = true;
					this.lastMeasurementRequestId++;
					BlexonErpApi.measure(
						this.store.article.id,
						selectedFaces.map((f) => ({
							exportId: f.originalId,
							order: this.getOrderOfMeasureResult(f),
						})),
						selectedEdges.map((f) => ({
							exportId: f.originalId,
							order: this.getOrderOfMeasureResult(f),
						})),
						this.lastMeasurementRequestId
					).then((result) => {
						// Only update the result if this is the result for the latest measurement request
						if (result.requestId === this.lastMeasurementRequestId) {
							this.lastMeasurementRequestId = 0;
							this.store.measureResult = result;
							this.store.loadingMeasurements = false;
						}
					});
				}
			}

			if (this.store.editorMode === EditorMode.WeldMode) {
				const selectedFace: IFace | undefined = this.allFaces.find(
					(f) => hoveredGroupId === f.groupId
				);
				if (selectedFace === undefined) {
					// The hovered element is not a face. Ignore it.
					return;
				}
				// If there are no welding pairs yet, or the latest pair is complete,
				// a new empty array for the new pair is created.
				if (!this.store.hasWeldingPairs || this.store.hasPairedWeldingFaces) {
					this.store.weldingPairs.push([]);
				}
				// Get the index of the clicked face in the current pair
				const indexOfWeldFace = this.store.currentWeldingPair.findIndex(
					(f) => hoveredGroupId === f.groupId
				);
				// Get the index in the selected group ids
				const indexOfSelectedGroupId = this.store.selectedGroupIds.indexOf(hoveredGroupId);
				if (indexOfWeldFace === -1) {
					// The clicked face doesn't exist in the current pair yet. Add it.
					this.store.currentWeldingPair.push(selectedFace);
					// Group id is added in the selectedGroupIds.
					this.store.selectedGroupIds.push(hoveredGroupId);
					this.setFaceColorById(hoveredGroupId, this.faceSelectedColor);
				} else {
					// The clicked face is already part of the current pair. Remove it.
					this.store.currentWeldingPair.splice(indexOfWeldFace, 1);
					this.store.selectedGroupIds.splice(indexOfSelectedGroupId, 1);
					this.setFaceColorById(hoveredGroupId, null);
				}
				// Check if the current welding pair has been completed
				if (this.store.hasPairedWeldingFaces) {
					// Deselect the faces from the last pair
					for (const face of this.store.currentWeldingPair) {
						const groupIdOfFaceIndex = this.store.selectedGroupIds.indexOf(
							face.groupId
						);
						if (groupIdOfFaceIndex !== -1) {
							this.store.selectedGroupIds.splice(groupIdOfFaceIndex, 1);
							this.setFaceColorById(face.groupId, null);
						}
					}
					// Check if the newly defined welding pair was already defined
					const otherPairs = this.store.weldingPairs.slice(
						0,
						this.store.weldingPairs.length - 1
					);
					if (otherPairs.length > 0) {
						const pairAlreadyExists = otherPairs.some((pair: IFace[]) =>
							pair.every((face: IFace) =>
								this.store.currentWeldingPair.map((wp) => wp.id).includes(face.id)
							)
						);
						// Delete the latest weld if the exact same faces are already defined.
						if (pairAlreadyExists) {
							this.store.weldingPairs.splice(this.store.weldingPairs.length - 1, 1);
						}
					}
				}
			}

			if (this.store.editorMode === EditorMode.RollDirectionMode) {
				const selectedEdges: IBrep[] = this.allEdges.filter(
					(e) => hoveredGroupId === e.groupId
				);

				if (selectedEdges.length > 0) {
					BlexonErpApi.setRollDirection({
						articleId: this.store.article.id,
						angle: this.store.rollDirection.angle,
						edgeExportId: selectedEdges[0].originalId,
					}).then((errorMessage) => {
						if (errorMessage) {
							this.store.errorDialog = {
								title: i18n.global.t("common.errors.rollDirectionAdjustFailedTitle"),
								messages: [errorMessage!],
							};
						} else {
							this.store.rollDirection.edgeId = null; // führt zu einem reload the schliffrichtung
							this.store.rollDirectionLoaded = false;
							this.store.isFileSaved = false;
						}
					});
				}
			}
		}
		if (this.hoveredGroupIds.length <= 0) {
			const hoveredWeldline = this.store.weldlines.find((w) => w.hovered);
			if (hoveredWeldline) {
				hoveredWeldline.selected = true;
			} else {
				this.deselectAll();
			}
		}
	}

	/**
	 * Sets the angle of the roll direction on current edge in the backend.
	 * EdgeId and angle must already be defined in the store.
	 */
	public async setRollDirectionAngle() {
		const errorMessage = await BlexonErpApi.setRollDirection({
			articleId: this.store.article.id,
			angle: this.store.rollDirection.angle,
			edgeExportId: this.store.rollDirection.edgeId!,
		});
		if (errorMessage) {
			this.store.errorDialog = {
				title: i18n.global.t("common.errors.rollDirectionAngleAdjustFailedTitle"),
				messages: [errorMessage!],
			};
		} else {
			this.store.isFileSaved = false;
		}
	}

	public deselectAll() {
		// Alles deselektieren
		this.store.emptySelectedFeatures();

		this.store.emptyCurrentWeldingPair();
		this.store.weldlines.forEach((w) => (w.selected = false));
		this.store.measureResult = null;

		// remember what was selected before
		const toRemoveGroups = this.store.selectedGroupIds.slice();
		this.store.emptySelectedGroupIds();

		// Bei grossen Modellen kommt es vor, dass die Rücksetzung auf die normale Farbe lange dauert.
		// Bei kleinen Modellen geht es ohne diese Zeilen. Komisch.
		const deselectEdges = this.allEdges.filter((e) => toRemoveGroups.includes(e.groupId));
		const deselectFaces = this.allFaces.filter((e) => toRemoveGroups.includes(e.groupId));
		let resetState = false;
		if (deselectEdges.length + deselectFaces.length > 50) {
			this.store.setLoadingState(LoadingState.MarkFeatures);
			resetState = true
		}
		setTimeout(() => {
			this.setFacesColor(deselectFaces, null);	
			this.setEdgesColor(deselectEdges, null);
			if (resetState) {
				// Sonst fummelt das zu viel rein.
				this.store.setLoadingState(LoadingState.Done);
			}
		}, 0);
	}

	/**
	 * Gets all edges that have one of the specified group ids.
	 * In case of a spline or curve more then one edge is returned. All
	 * those edges form an edge in space claim.
	 * @param groupIds
	 * @returns
	 */
	public getEdgesByGroupId(groupId: number): IEdge[] {
		return this.allEdges.filter((e) => groupId == e.groupId);
	}

	/**
	 * Gets all group ids that belong to the specified group id.
	 * If the specified group id belongs to a feature, all group ids
	 * of all faces and edges will be returned.
	 * @param groupId
	 * @returns
	 */
	public getAllGroupIds(groupId: number): number[] {
		const allBreps = (this.allEdges as IBrep[]).concat(this.allFaces);
		var breps = allBreps.filter((f) => f.groupId == groupId);
		return breps.map((f) => f.groupId);
	}

	/**
	 * Gibt alle Gruppen Ids von faces und edges zurück, die zum Feature gehören.
	 */
	public getGroupIdsByExportId(exportId: string): number[] {
		var facesGroups = this.allFaces.filter((f) => f.id == exportId).map((f) => f.groupId);
		var edgesGroups = this.allEdges.filter((e) => e.id == exportId).map((e) => e.groupId);
		return [...new Set(facesGroups.concat(edgesGroups))];
	}

	/**
	 * Gibt die originalen export ids anhand der normalisierten export ids zurück für die ausgewählten features.
	 */
	public getOriginalExportIdsForSelectedFeatures(exportIds: string[]): string[] {
		// get relevant faces and edges from selected features
		const allElements = (this.allFaces as IBrep[]).concat(this.allEdges);

		const originalIds = allElements
			.filter((e) => exportIds.some((id) => id == e.id))
			.map((e) => e.originalId);
		return originalIds;
	}

	/**
	 * Gibt die originalen export ids anhand der normalisierten export ids zurück
	 * @param exportIds Normalisierte export ids
	 */
	public getOriginalExportIds(exportIds: string[]): string[] {
		const allElements: any = this.allFaces.concat(this.allEdges as any);
		const originalIds = allElements
			.filter((e: any) => exportIds.some((id) => id == e.id))
			.map((e: any) => e.originalId);
		return originalIds;
	}

	/**
	 * Gibt die Gruppen Id der Seite zurück, die den spezifizierten Punkt enthält.
	 * Eine Seite besteht aus vielen Dreiecken und jedes Dreieck besteht aus 3 Punkten (Vertices)
	 * Da Spaceclaim die Seiten in Dreiecke zerlegt, kommt hier nur eine Gruppen Id einer Seite zurück,
	 * da eine Seite auch eine Gruppe bildet.
	 * @param verticeIndex
	 * Beliebiger Punkt eines beliebigen Dreiecks der Seite
	 * @returns
	 * Gruppen Id der Seite
	 */
	public getGroupIdByVerticeId(verticeIndex: number): number {
		var face = this.allFaces.find((f) => f.indexes.includes(verticeIndex));
		if (face != undefined) {
			return face.groupId;
		} else {
			return 0;
		}
	}

	public getFaceByFaceIndex(faceIndex: number, meshUuid: string): IFace | null {
		var body = this.displayedBodies.find((b) => b.mesh.uuid == meshUuid);
		if (body) {
			// The face index starts at zero for every mesh but in the model we have unique indexes.
			// The face index needs to be offset by the start index of the body.
			var minimum = body.firstIndex;
			// A face consists of three points. Every point as an index. Due to this, the index of points
			// is three times higher than the face index
			var face = body.faces.find((f) => f.indexes.includes(faceIndex * 3 + minimum));
			if (face != undefined) {
				return face;
			} else {
				return null;
			}
		}
		return null;
	}

	/**
	 * Gets all faces with the specified group id.
	 * This is normally just one face which consists of a lot of triangles.
	 * @param groupIds
	 * @returns
	 */
	public getFaceAndBodyByGroupId(groupId: number): { face: IFace; body: IBody } | null {
		for (const body of this.displayedBodies) {
			var face = body.faces.find((f) => f.groupId == groupId);
			if (face) {
				return { face, body };
			}
		}
		return null;
	}

	public getColor(color: number) {
		const colorObject = {
			red: (color >> 16) & 0xff,
			green: (color >> 8) & 0xff,
			blue: color & 0xff,
		};
		return new THREE.Color(
			`rgba(${colorObject.red}, ${colorObject.green}, ${colorObject.blue}, 1)`
		);
	}

	public getMeshes(): THREE.Mesh[] {
		const meshes = [];
		const allBends = this.bendBodies;
		for (const body of this.displayedBodies) {
			let offset = false;
			if (allBends.find((b) => b == body)) {
				offset = true;
			}
			body.firstIndex = this.index;

			for (const face of body.faces) {
				const mesh = this.createMesh([face], offset);
				meshes.push(mesh);
				face.mesh = mesh;
				body.mesh = mesh; // is now irrelevant
			}
		}

		return meshes;
	}

	/**
	 * Creates a single mesh as buffer geometry for all faces and fills feature and groupIds
	 * into the buffer to enable raycasting.
	 */
	private createMesh(faces: IFace[], offset: boolean): any {
		const positions = [];
		const normals = [];
		const uvs = [];
		const meshColors = [];
		const groupIds = [];

		const material = new THREE.MeshBasicMaterial({ vertexColors: true });
		for (const face of faces) {
			face.indexes = [];
			var faceColor = this.getColor(face.argbColor);
			for (const vertex of face.points) {
				positions.push(...vertex.pos);
				normals.push(...vertex.norm);
				uvs.push(...vertex.uv);
				groupIds.push(face.groupId);
				meshColors.push(...faceColor.toArray());
				// Pro Dreieck gibt es auch drei Index
				face.indexes.push(this.index);
				this.index++;
			}
			const geometry = new THREE.BufferGeometry();

			const positionNumComponents = 3;
			const normalNumComponents = 3;
			const uvNumComponents = 2;
			const colorNumComponents = 3;
			geometry.setAttribute(
				"position",
				new THREE.BufferAttribute(new Float32Array(positions), positionNumComponents)
			);
			geometry.setAttribute(
				"normal",
				new THREE.BufferAttribute(new Float32Array(normals), normalNumComponents)
			);
			geometry.setAttribute(
				"uv",
				new THREE.BufferAttribute(new Float32Array(uvs), uvNumComponents)
			);
			geometry.setAttribute(
				"color",
				new THREE.BufferAttribute(new Float32Array(meshColors), colorNumComponents)
			);
			geometry.setAttribute(
				"groupId",
				new THREE.BufferAttribute(new Float32Array(groupIds), 1)
			);

			//@ts-ignore
			material.polygonOffset = true;
			let name = "Body";
			if (offset) {
				material.polygonOffsetFactor = -0.02;
				name = "Bendzone";
			} else {
				material.polygonOffsetFactor = 1;
			}

			if (face.argbColor === -13395508) {
				face.isAnchorFace = true;
			}

			const mesh = new THREE.Mesh(geometry, material);
			geometry.computeBoundsTree();

			mesh.name = name;
			mesh.userData = face;
			return mesh;
		}
	}

	public getContourAsMesh() {
		// Variante mit Line2
		var contours = [];
		var index = 0;
		for (const edge of this.allEdges) {
			const lineMaterial = new LineMaterial({
				color: 0x000000,
				linewidth: 1.5, // in world units with size attenuation, pixels otherwise
				vertexColors: false,
				dashed: false,
				alphaToCoverage: false,
			});
			var points: any = [];
			edge.indexes = [];
			for (const point of edge.points) {
				points.push(point.x, point.y, point.z);
				edge.indexes.push(index);
				index++;
			}
			const lineGeometry = new LineGeometry();
			lineGeometry.setPositions(points);
			const line = new Line2(lineGeometry, lineMaterial);
			lineGeometry.computeBoundsTree();
			line.userData = edge;
			edge.mesh = line;
			contours.push(line);
		}

		return contours;
	}

	/**
	 * Shows a pulsating red dot at the centroid of each face whose normalized export id
	 * matches. Thin convenience wrapper around {@link PulseHighlighter} for callers that
	 * only have export ids at hand (validation messages).
	 */
	public pulseHighlightFaces(exportIds: string[]) {
		if (exportIds.length === 0) {
			return;
		}
		const faces = this.allFaces.filter((f) => exportIds.includes(f.id));
		this.pulseHighlighter.pulse(faces);
	}

	public getOrderOfMeasureResult(element: IBrep): any {
		if (this.store.measureResult) {
			const result = this.store.measureResult.elementMeasureResults.find(
				(r) => r.exportId == element.originalId
			);
			if (result) {
				return result.orderPosition;
			}
			return this.store.measureResult.elementMeasureResults.length;
		}
		return 0;
	}
}
